import { useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import type { Stripe, StripeElements } from '@stripe/stripe-js';
import { publicPaymentsService, apiErrorMessage } from '../services/api';
import { accent, fontFamily, grey, radius, semantic } from '../theme/tokens';

/**
 * Stripe's Payment Element for the public pay page — the Stripe counterpart of
 * `useSquareWebPayments`.
 *
 * The server creates (or reuses) a PaymentIntent for the outstanding balance;
 * this mounts the element against its client secret into
 * `#stripe-payment-element`, and `confirm()` hands the card to Stripe.js,
 * which runs 3-D Secure itself. The amount is never chosen here.
 */

interface UseStripePaymentElementOptions {
  invoiceId?: string;
  publishableKey?: string;
  /** The element only mounts while this is true. */
  enabled: boolean;
}

export function useStripePaymentElement({
  invoiceId,
  publishableKey,
  enabled,
}: UseStripePaymentElementOptions) {
  const theme = useTheme();
  const stripeRef = useRef<Stripe | null>(null);
  const elementsRef = useRef<StripeElements | null>(null);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const dark = theme.palette.mode === 'dark';

  useEffect(() => {
    if (!enabled || !invoiceId || !publishableKey) return;

    let cancelled = false;
    let unmount: (() => void) | undefined;

    (async () => {
      try {
        const [stripe, intent] = await Promise.all([
          loadStripe(publishableKey),
          publicPaymentsService.createStripeIntent(invoiceId),
        ]);
        if (cancelled) return;
        if (!stripe) throw new Error('Stripe failed to load');

        // Styled from the design tokens so the iframe doesn't read as foreign.
        const elements = stripe.elements({
          clientSecret: intent.clientSecret,
          appearance: {
            theme: dark ? 'night' : 'stripe',
            variables: {
              colorPrimary: dark ? accent[400] : accent[600],
              colorDanger: semantic[dark ? 'dark' : 'light'].danger.main,
              colorText: dark ? grey[100] : grey[900],
              fontFamily,
              borderRadius: `${radius.control}px`,
            },
          },
        });
        const element = elements.create('payment', { layout: 'tabs' });
        element.on('ready', () => !cancelled && setReady(true));
        element.mount('#stripe-payment-element');
        unmount = () => element.destroy();

        stripeRef.current = stripe;
        elementsRef.current = elements;
      } catch (err) {
        if (!cancelled) {
          setLoadError(apiErrorMessage(err, 'Online payment is unavailable right now'));
        }
      }
    })();

    return () => {
      cancelled = true;
      setReady(false);
      unmount?.();
      stripeRef.current = null;
      elementsRef.current = null;
    };
  }, [enabled, invoiceId, publishableKey, dark]);

  /**
   * Confirms the payment. Resolves to the PaymentIntent id, or throws with a
   * message the customer can act on ("Your card was declined.").
   */
  const confirm = async (): Promise<string> => {
    const stripe = stripeRef.current;
    const elements = elementsRef.current;
    if (!stripe || !elements) throw new Error('The card form is not ready yet');

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      // Cards finish in place (3-D Secure opens as a modal). Redirect-based
      // methods come back to this page, which confirms on load.
      redirect: 'if_required',
      confirmParams: { return_url: window.location.href },
    });
    if (error) throw new Error(error.message || 'The payment could not be completed');
    if (!paymentIntent) throw new Error('Stripe did not return a payment');
    return paymentIntent.id;
  };

  return { ready, loadError, confirm };
}
